/**
 * Gasherd in der Seitenansicht: Kochmulde, Brennerkrone mit Flammen,
 * Topfträger und ein Drehknopf an der Vorderkante der Arbeitsfläche.
 *
 * Die Hitze wird über die Größe der Flammen und die Stellung des Knopfs
 * gezeigt. Gefäße stehen mit dem Boden auf GRATE_Y.
 */
import { lerp } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { circle, group, line, rect, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { flame } from './effects.ts';
import { GROUND_Y } from './stage.ts';

/** Oberkante der Kochmulde. */
export const HOB_Y = GROUND_Y - 4;

/** Höhe, auf der Gefäße auf dem Topfträger stehen. */
export const GRATE_Y = GROUND_Y - 21;

/** Hitzestufen für Parameter. */
export const HEAT_LEVELS = { low: 'schwach', medium: 'mittel', high: 'stark' } as const;

/** Kennung einer Hitzestufe. */
export type HeatLevel = keyof typeof HEAT_LEVELS;

/** Flammenstärke je Hitzestufe. */
export const HEAT_POWER: Readonly<Record<HeatLevel, number>> = { low: 0.3, medium: 0.62, high: 1 };

/** Liest die Hitzestufe aus dem Schritttext. */
export function heatFromText(text: string): { heat?: HeatLevel } {
  const lower = text.toLowerCase();
  if (/(schwach|niedrig|klein)\w* (hitze|stufe|flamme)|leise/.test(lower)) return { heat: 'low' };
  if (/mittl\w* (hitze|stufe)/.test(lower)) return { heat: 'medium' };
  if (/(stark|hoh|groß|gross)\w* (hitze|stufe|flamme)|scharf/.test(lower)) return { heat: 'high' };
  return {};
}

/**
 * Kochstelle unter einem Gefäß.
 * @param cx Mitte des Brenners.
 * @param power Flammenstärke 0 (aus) bis 1.
 * @returns back: Mulde, Krone und Flammen (vor dem Gefäß zeichnen);
 *          front: Topfträger (nach dem Gefäß zeichnen).
 */
export function stove(cx: number, power: number, t: number): { back: SceneNode[]; front: SceneNode[] } {
  const flames: SceneNode[] = [];
  if (power > 0.02) {
    const count = 6;
    const width = 44;
    for (let index = 0; index < count; index += 1) {
      const x = cx - width / 2 + (width / (count - 1)) * index;
      flames.push(flame(x, GROUND_Y - 10, 5 + 10 * power, t, index * 1.3));
    }
  }
  const prong = (x: number): SceneNode => rect(x - 3, GRATE_Y, 6, HOB_Y - GRATE_Y, 1, styles.solid('ink'));
  return {
    back: [
      // Kochmulde als flache Platte
      rect(6, HOB_Y, 228, 5, 2, styles.solid('shade')),
      // Brennerkrone
      rect(cx - 26, GROUND_Y - 10, 52, 6, 2, styles.solid('deep')),
      ...flames,
    ],
    front: [prong(cx - 48), prong(cx + 48), line(cx - 52, GRATE_Y, cx + 52, GRATE_Y, styles.detail())],
  };
}

/**
 * Drehknopf an der Vorderkante. Die Markierung zeigt die Stufe: senkrecht
 * nach oben ist aus, eine Dreivierteldrehung ist volle Hitze.
 */
export function stoveKnob(cx: number, power: number): SceneNode {
  const cy = GROUND_Y + 11;
  const angle = lerp(0, 240, power);
  return group([
    circle(cx, cy, 7, styles.solid('paper')),
    group([line(cx, cy - 1.5, cx, cy - 5.5, { stroke: 'ink', strokeWidth: STROKE.detail })], {
      transform: { rotate: angle, ox: cx, oy: cy },
    }),
    // Kontrollpunkt: leuchtet, solange die Flamme brennt
    circle(cx - 13, cy, 2.4, { fill: power > 0.02 ? 'accent' : 'shade', stroke: 'none' }),
  ]);
}
